import create from 'zustand';
import { devtools } from 'zustand/middleware';
import {
  SvgCanvasData,
  SvgCanvasElement,
  SvgNodeElement,
  SvgGroupElement,
  SelectedState,
  SvgAsset,
  SvgAiHistory,
  SvgAnimationProps,
  SvgSubElement,
  CanvasViewport,
} from '../types/svgTypes';
import svgAPIService from '../services/svgApi';
import { parseSvgContent, rebuildSvgContent, parseSvgFragment } from '../utils/svgParser';
import { extractAnimationFromSvg } from '../utils/svgAnimationUtils';

// 本地素材缓存 key
const LOCAL_ASSET_STORAGE_KEY = 'ai_svg_assets_local';

const loadLocalAssetsSafely = (): SvgAsset[] => {
  try {
    if (typeof window === 'undefined') {
      return [];
    }
    const raw = window.localStorage.getItem(LOCAL_ASSET_STORAGE_KEY);
    if (!raw) {
      return [];
    }
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed as SvgAsset[];
  } catch (error) {
    console.error('从 localStorage 读取 SVG 素材失败:', error);
    return [];
  }
};

const saveLocalAssetsSafely = (assets: SvgAsset[]): void => {
  try {
    if (typeof window === 'undefined') {
      return;
    }
    window.localStorage.setItem(
      LOCAL_ASSET_STORAGE_KEY,
      JSON.stringify(assets),
    );
  } catch (error) {
    console.error('写入 localStorage SVG 素材失败:', error);
  }
};

/** 缩放限制 */
const MIN_ZOOM = 0.1;
const MAX_ZOOM = 5.0;
const ZOOM_STEP = 0.1;
const ELEMENT_SCALE_STEP = 0.05;
export const MIN_ELEMENT_SCALE = 0.1;
export const MAX_ELEMENT_SCALE = 10;

interface SvgStoreState {
  canvas?: SvgCanvasData;
  isLoading: boolean;
  error?: string;

  selectedState: SelectedState;

  undoStack: SvgCanvasData[];
  redoStack: SvgCanvasData[];

  assets: SvgAsset[];
  aiHistories: SvgAiHistory[];

  // ========== 画布视口 ==========
  viewport: CanvasViewport;

  // ========== 子元素编辑模式状态 ==========
  /** 是否处于子元素编辑模式 */
  isSubElementEditMode: boolean;
  /** 当前编辑的节点 ID */
  editingNodeId: string | null;
  /** 当前选中的子元素 ID */
  selectedSubElementId: string | null;

  // actions
  initNewCanvas: (width?: number, height?: number) => void;
  loadCanvas: (canvasId: string) => Promise<void>;
  saveCanvas: (asFile?: boolean) => Promise<void>;
  exportSvgFile: (svgHtml: string) => void;

  addElement: (element: SvgCanvasElement) => void;
  updateElement: (id: string, partial: Partial<SvgCanvasElement>) => void;
  updateElements: (updates: { id: string; partial: Partial<SvgCanvasElement> }[], skipUndo?: boolean) => void;
  removeElements: (ids: string | string[]) => void;

  setSelectedIds: (ids: string[]) => void;
  setActiveElement: (id?: string) => void;
  
  // High-level operations
  alignSelectedElements: (type: 'left' | 'center' | 'right' | 'top' | 'middle' | 'bottom') => void;

  undo: () => void;
  redo: () => void;

  loadAssets: () => Promise<void>;
  addAsset: (asset: Partial<SvgAsset>) => Promise<SvgAsset>;
  deleteAsset: (id: string) => Promise<void>;

  addAiHistory: (history: Partial<SvgAiHistory>) => Promise<void>;
  loadAiHistories: () => Promise<void>;

  addAiSvgToCanvas: (svgContent: string, name?: string, options?: { x?: number; y?: number }) => void;

  /** 进入子元素编辑模式 */
  enterSubElementEditMode: (nodeId: string) => void;
  /** 退出子元素编辑模式 */
  exitSubElementEditMode: () => void;
  /** 选中子元素 */
  selectSubElement: (subElementId: string | null) => void;
  /** 更新子元素属性 */
  updateSubElement: (nodeId: string, subElementId: string, attrs: Partial<SvgSubElement['customAttributes']>) => void;
  /** 解析节点的子元素 */
  parseNodeSubElements: (nodeId: string) => void;
  /** 更新节点动画属性 */
  updateNodeAnimation: (nodeId: string, animation: Partial<SvgAnimationProps>) => void;
  /** 恢复子元素的原始属性 */
  resetSubElementAttribute: (nodeId: string, subElementId: string, attribute: 'fill' | 'stroke' | 'strokeWidth' | 'opacity' | 'textContent' | 'all') => void;

  // ========== 新增 actions ==========
  /** 设置视口缩放 */
  setZoom: (zoom: number) => void;
  /** 设置视口平移 */
  setPan: (panX: number, panY: number) => void;
  /** 以某个点为中心进行缩放 */
  zoomAtPoint: (delta: number, pointX: number, pointY: number) => void;
  /** 重置视口（可传入计算好的 viewport，否则恢复默认） */
  resetViewport: (viewport?: { zoom: number; panX: number; panY: number }) => void;
  /** 缩放选中元素 */
  scaleSelectedElements: (delta: number) => void;
  /** 组合选中元素 */
  groupElements: (ids: string[]) => void;
  /** 解组 */
  ungroupElements: (groupId: string) => void;
  /** ★ 新增：拆分 SVG 节点为多个独立子元素 */
  splitElement: (nodeId: string) => void;
  /** 清空画布所有元素，保留画布尺寸和背景 */
  clearCanvas: () => void;
}

const createEmptyCanvas = (width: number, height: number): SvgCanvasData => ({
  canvasId: `canvas_${Date.now()}`,
  width,
  height,
  background: '#ffffff',
  elements: [],
});

const cloneCanvas = (canvas: SvgCanvasData): SvgCanvasData =>
  JSON.parse(JSON.stringify(canvas));

/**
 * ★ 新增工具函数：将单个 SVG 子图形（path/circle/rect 等）序列化为独立的 svgContent 字符串
 */
const serializeElementToSvgContent = (element: Element): string => {
  return element.outerHTML;
};

/**
 * ★ 新增工具函数：解析 SVG 内容，提取每个顶层可视图形元素
 * 返回 { svgContent, bbox } 数组
 */
const VISUAL_TAGS = new Set(['path', 'circle', 'rect', 'line', 'ellipse', 'polygon', 'polyline', 'g', 'text', 'image', 'use']);
const SKIP_SPLIT_TAGS = new Set(['defs', 'filter', 'clippath', 'mask', 'symbol', 'style', 'animate', 'animatetransform', 'animatemotion', 'set', 'lineargradient', 'radialgradient', 'stop', 'femerge', 'femergenode', 'fegaussianblur', 'title', 'desc', 'metadata']);

interface SplitChildInfo {
  svgContent: string;
  defsContent: string;
  name: string;
}

/**
 * 从一个父元素中收集所有直接可视子元素
 * 跳过 defs/动画等非可视标签
 */
const collectVisualChildren = (parent: Element, defsContent: string): SplitChildInfo[] => {
  const results: SplitChildInfo[] = [];

  for (const child of Array.from(parent.children)) {
    const tag = child.tagName.toLowerCase();

    // 跳过非可视元素
    if (SKIP_SPLIT_TAGS.has(tag)) continue;

    if (VISUAL_TAGS.has(tag)) {
      const content = serializeElementToSvgContent(child);
      const id = child.getAttribute('id') || tag;
      results.push({
        svgContent: content,
        defsContent,
        name: id,
      });
    }
  }

  return results;
};

/**
 * ★ 修复后的 extractSplitChildren
 * 
 * 改进点：
 * 1. 检测 DOMParser 解析错误
 * 2. 如果顶层只有一个 <g> 包裹元素，自动展开到其子元素层级
 * 3. 递归展开嵌套的单一 <g>，直到找到多个可视子元素
 */
const extractSplitChildren = (svgContent: string): SplitChildInfo[] => {
  const svgRoot = parseSvgFragment(svgContent);

  // 1. 提取所有 <defs> 内容
  let defsContent = '';
  const defsElements = svgRoot.querySelectorAll('defs');
  defsElements.forEach(d => { defsContent += d.outerHTML; });

  // 2. 收集顶层可视子元素（排除 defs 等）
  const findSplittableChildren = (parent: Element): SplitChildInfo[] => {
    const visualChildren = collectVisualChildren(parent, defsContent);

    // ★ 关键修复：如果只找到 1 个可视子元素，且它是 <g>，则递归展开
    if (visualChildren.length === 1) {
      // 找到对应的 DOM 元素来检查是否是 <g>
      const singleChild = Array.from(parent.children).find(child => {
        const tag = child.tagName.toLowerCase();
        return VISUAL_TAGS.has(tag) && !SKIP_SPLIT_TAGS.has(tag);
      });

      if (singleChild && singleChild.tagName.toLowerCase() === 'g') {
        // 递归展开这个 <g> 的子元素
        const innerChildren = findSplittableChildren(singleChild);
        if (innerChildren.length > 1) {
          return innerChildren;
        }
      }
    }

    // ★ 如果顶层没有找到任何可视子元素，尝试递归搜索
    if (visualChildren.length === 0) {
      const deepResults: SplitChildInfo[] = [];
      for (const child of Array.from(parent.children)) {
        const tag = child.tagName.toLowerCase();
        if (SKIP_SPLIT_TAGS.has(tag)) continue;

        // 非可视标签，但可能包含可视子元素（如 <a>、<svg> 嵌套等）
        if (!VISUAL_TAGS.has(tag)) {
          deepResults.push(...findSplittableChildren(child));
        }
      }
      if (deepResults.length > 0) {
        return deepResults;
      }
    }

    return visualChildren;
  };

  const results = findSplittableChildren(svgRoot);

  console.log('extractSplitChildren: 解析结果', {
    inputLength: svgContent.length,
    topLevelChildCount: svgRoot.children.length,
    extractedCount: results.length,
    names: results.map(r => r.name),
  });

  return results;
};

/**
 * ★ 修复：判断一个 SVG 内容是否可拆分
 * 直接复用 extractSplitChildren 的结果，确保与实际拆分逻辑一致
 * 性能由组件侧 useMemo 的精准依赖（selectedSvgContent）保证
 */
export const canSplitSvgContent = (svgContent: string): boolean => {
  try {
    const children = extractSplitChildren(svgContent);
    return children.length > 1;
  } catch {
    return false;
  }
};

/**
 * ★ 修复：将 SVG 内容片段包装为完整的 SVG 文件并触发下载
 * 改进：支持传入 viewBox 以正确定位内容
 */
export const downloadSvgContent = (
  svgContent: string,
  fileName: string,
  width?: number,
  height?: number,
  viewBox?: { x: number; y: number; width: number; height: number }
): void => {
  // 检查 svgContent 是否已经是完整的 <svg> 标签
  const trimmed = svgContent.trim();
  if (trimmed.startsWith('<svg') && trimmed.endsWith('</svg>')) {
    // 已经是完整的 SVG，直接下载
    const fullSvg = `<?xml version="1.0" encoding="UTF-8"?>\n${trimmed}`;
    triggerDownload(fullSvg, fileName);
    return;
  }

  // 尝试从内容中解析尺寸
  const dims = parseSvgDimensions(svgContent);
  const w = width ?? dims.width;
  const h = height ?? dims.height;

  // ★ 修复：使用传入的 viewBox，或基于内容解析的尺寸
  const vb = viewBox
    ? `${viewBox.x} ${viewBox.y} ${viewBox.width} ${viewBox.height}`
    : `0 0 ${w} ${h}`;

  // 包装为完整的 SVG 文件
  const fullSvg = `<?xml version="1.0" encoding="UTF-8"?>
    <svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" width="${w}" height="${h}" viewBox="${vb}">
    ${svgContent}
    </svg>`;

  triggerDownload(fullSvg, fileName);
};


/** 从 SVG 片段中解析实际尺寸 */
const parseSvgDimensions = (content: string): { width: number; height: number } => {
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(
      `<svg xmlns="http://www.w3.org/2000/svg">${content}</svg>`,
      'image/svg+xml'
    );
    const root = doc.documentElement;

    // 如果内容本身是嵌套的 svg 元素
    const innerSvg = root.querySelector('svg');
    if (innerSvg) {
      const vb = innerSvg.getAttribute('viewBox');
      if (vb) {
        const parts = vb.split(/[\s,]+/).map(Number);
        if (parts.length === 4 && parts[2] > 0 && parts[3] > 0) {
          return { width: parts[2], height: parts[3] };
        }
      }
      const w = parseFloat(innerSvg.getAttribute('width') ?? '');
      const h = parseFloat(innerSvg.getAttribute('height') ?? '');
      if (w > 0 && h > 0) return { width: w, height: h };
    }

    // 使用 getBBox - 需要临时插入 DOM
    // 回退：用内容中的 viewBox 信息
    const viewBoxAttr = content.match(/viewBox\s*=\s*["']([^"']+)["']/);
    if (viewBoxAttr) {
      const parts = viewBoxAttr[1].split(/[\s,]+/).map(Number);
      if (parts.length === 4 && parts[2] > 0 && parts[3] > 0) {
        return { width: parts[2], height: parts[3] };
      }
    }
  } catch {
    // 解析失败，使用默认值
  }
  return { width: 200, height: 200 };
};

/** 触发浏览器下载 */
const triggerDownload = (content: string, fileName: string): void => {
  const blob = new Blob([content], { type: 'image/svg+xml;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName.endsWith('.svg') ? fileName : `${fileName}.svg`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

/**
 * ★ 新增：确保 SVG 内容可被预览
 * 如果 svgContent 不包含 <svg> 根标签，则包装一层
 */
export const ensurePreviewableSvg = (svgCode: string | null | undefined): string => {
  if (!svgCode) return '';
  const trimmed = svgCode.trim();
  if (trimmed.startsWith('<svg')) return trimmed;
  // 用一个足够大的 viewBox 包裹，让内容自适应
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 200">${trimmed}</svg>`;
};


export const useSvgStore = create<SvgStoreState>()(
  devtools((set, get) => ({
    canvas: undefined,
    isLoading: false,
    error: undefined,

    selectedState: {
      selectedIds: [],
    },

    undoStack: [],
    redoStack: [],

    assets: [],
    aiHistories: [],

    // ========== 新增状态初始值 ==========
    viewport: { zoom: 1, panX: 0, panY: 0 },

    // ========== 新增状态初始值 ==========
    isSubElementEditMode: false,
    editingNodeId: null,
    selectedSubElementId: null,

    initNewCanvas: (width = 1200, height = 800) => {
      const newCanvas = createEmptyCanvas(width, height);
      set({
        canvas: newCanvas,
        undoStack: [],
        redoStack: [],
        selectedState: { selectedIds: [] },
        viewport: { zoom: 1, panX: 0, panY: 0 },
        error: undefined,
      });
    },

    clearCanvas: () => {
      const { canvas } = get();
      if (!canvas) return;
      const cleared = { ...canvas, elements: [] };
      set({
        canvas: cleared,
        undoStack: [],
        redoStack: [],
        selectedState: { selectedIds: [] },
        viewport: { zoom: 1, panX: 0, panY: 0 },
      });
    },

    loadCanvas: async (canvasId: string) => {
      set({ isLoading: true, error: undefined });
      try {
        const record = await svgAPIService.getCanvasById(canvasId);
        set({
          canvas: record.data,
          undoStack: [],
          redoStack: [],
          selectedState: { selectedIds: [] },
          viewport: { zoom: 1, panX: 0, panY: 0 },
        });
      } catch (error: any) {
        console.error('加载画布失败:', error);
        set({ error: error.message ?? '加载画布失败' });
      } finally {
        set({ isLoading: false });
      }
    },

    saveCanvas: async (asFile = false) => {
      const { canvas } = get();
      if (!canvas) {
        return;
      }
      
      if (asFile) {
        svgAPIService.downloadCanvasAsJson(canvas);
        return;
      }

      set({ isLoading: true, error: undefined });
      try {
        await svgAPIService.saveCanvasSnapshot(canvas.canvasId, canvas);
      } catch (error: any) {
        console.error('保存画布失败:', error);
        set({ error: error.message ?? '保存画布失败' });
      } finally {
        set({ isLoading: false });
      }
    },

    exportSvgFile: (svgHtml: string) => {
      const { canvas } = get();
      if (!canvas) return;
      // 这里的 svgHtml 通常是从 DOM 获取的 innerHTML
      svgAPIService.downloadCanvasAsSvg({
        width: canvas.width,
        height: canvas.height,
        innerHtml: svgHtml
      }, `export_${canvas.canvasId}.svg`);
    },

    addElement: (element: SvgCanvasElement) => {
      const { canvas, undoStack } = get();
      if (!canvas) {
        return;
      }
      const previous = cloneCanvas(canvas);
      const next: SvgCanvasData = {
        ...canvas,
        elements: [...canvas.elements, element],
      };
      set({
        canvas: next,
        undoStack: [...undoStack, previous],
        redoStack: [],
      });
    },

    updateElement: (id: string, partial: Partial<SvgCanvasElement>) => {
      const { canvas, undoStack } = get();
      if (!canvas) {
        return;
      }
      // clamp scale
      if ('scale' in partial && typeof (partial as any).scale === 'number') {
        (partial as any).scale = Math.round(
          Math.max(MIN_ELEMENT_SCALE, Math.min(MAX_ELEMENT_SCALE, (partial as any).scale)) * 100
        ) / 100;
      }
      const previous = cloneCanvas(canvas);
      const nextElements = canvas.elements.map((el) =>
        el.id === id ? ({ ...el, ...partial } as SvgCanvasElement) : el
      );
      set({
        canvas: {
          ...canvas,
          elements: nextElements,
        },
        undoStack: [...undoStack, previous],
        redoStack: [],
      });
    },

    /**
     * ★ 修复：支持 skipUndo 参数
     * 拖拽过程中 skipUndo=true，不推入 undoStack（mouseUp 时手动推入）
     */
    updateElements: (updates: { id: string; partial: Partial<SvgCanvasElement> }[], skipUndo?: boolean) => {
      const { canvas, undoStack } = get();
      if (!canvas || updates.length === 0) return;

      // clamp scale
      for (const u of updates) {
        if ('scale' in u.partial && typeof (u.partial as any).scale === 'number') {
          (u.partial as any).scale = Math.round(
            Math.max(MIN_ELEMENT_SCALE, Math.min(MAX_ELEMENT_SCALE, (u.partial as any).scale)) * 100
          ) / 100;
        }
      }

      const updatesMap = new Map(updates.map(u => [u.id, u.partial]));

      const nextElements = canvas.elements.map((el) => {
        const partial = updatesMap.get(el.id);
        if (partial) {
          return { ...el, ...partial } as SvgCanvasElement;
        }
        return el;
      });

      if (skipUndo) {
        // 拖拽中：只更新 canvas，不推 undo
        set({
          canvas: { ...canvas, elements: nextElements },
        });
      } else {
        const previous = cloneCanvas(canvas);
        set({
          canvas: { ...canvas, elements: nextElements },
          undoStack: [...undoStack, previous],
          redoStack: [],
        });
      }
    },

    removeElements: (ids: string) => {
      const { canvas, undoStack, selectedState } = get();
      if (!canvas) {
        return;
      }

      // 统一转换为数组
      const idsToRemove = Array.isArray(ids) ? ids : [ids];
      if (idsToRemove.length === 0) return;

      const previous = cloneCanvas(canvas);

      // ★ 修复：如果删除的是 group，也要删除其 children
      const allIdsToRemove = new Set(idsToRemove);
      canvas.elements.forEach(el => {
        if (el.type === 'group' && allIdsToRemove.has(el.id)) {
          (el as SvgGroupElement).children.forEach(childId => allIdsToRemove.add(childId));
        }
      });
      
      // 过滤掉所有在删除列表中的元素
      const nextElements = canvas.elements.filter((el) => !idsToRemove.includes(el.id));
      
      // 更新选中状态：从当前选中列表中移除已删除的 ID
      const nextSelectedIds = selectedState.selectedIds.filter(
        (selectedId) => !idsToRemove.includes(selectedId)
      );

      // 检查当前激活的元素是否被删除
      const isActiveRemoved = selectedState.activeElementId && idsToRemove.includes(selectedState.activeElementId);

      set({
        canvas: {
          ...canvas,
          elements: nextElements,
        },
        selectedState: {
          ...selectedState,
          selectedIds: nextSelectedIds,
          activeElementId: isActiveRemoved
              ? undefined // 如果被删除了，重置 activeElementId
              : selectedState.activeElementId,
        },
        undoStack: [...undoStack, previous],
        redoStack: [],
      });
    },


    setSelectedIds: (ids: string[]) => {
      set((state) => ({
        selectedState: {
          ...state.selectedState,
          selectedIds: ids,
        },
      }));
    },

    /**
     * ★ 修复：setActiveElement 不再覆盖 selectedIds
     * 只更新 activeElementId，selectedIds 由 setSelectedIds 单独管理
     */
    setActiveElement: (id?: string) => {
      set((state) => ({
        selectedState: {
          ...state.selectedState,
          activeElementId: id,
        },
      }));
    },
    
    // 对齐功能：目前仅支持对齐 x, y。更复杂的对齐需要 BoundingBox（需要 Render 完毕回填或解析 SVG）
    // 这里做最简单的假设：x/y 就是左上角。
    alignSelectedElements: (type) => {
      const { canvas, selectedState, undoStack } = get();
      if (!canvas || selectedState.selectedIds.length < 2) return;

      const selectedElements = canvas.elements.filter(el => selectedState.selectedIds.includes(el.id)) as SvgNodeElement[];
      if (selectedElements.length < 2) return;

      const previous = cloneCanvas(canvas);
      let targetValue = 0;

      // 1. 计算基准值
      if (type === 'left') {
        targetValue = Math.min(...selectedElements.map(el => el.x));
      } else if (type === 'top') {
        targetValue = Math.min(...selectedElements.map(el => el.y));
      } else if (type === 'right') {
         // 注意：没有 width 信息很难做右对齐，这里的简单实现假设对其 X 坐标最大值
         targetValue = Math.max(...selectedElements.map(el => el.x));
      } else if (type === 'bottom') {
         targetValue = Math.max(...selectedElements.map(el => el.y));
      } else if (type === 'center') {
         // 简易版：X 平均值
         const min = Math.min(...selectedElements.map(el => el.x));
         const max = Math.max(...selectedElements.map(el => el.x));
         targetValue = (min + max) / 2;
      } else if (type === 'middle') {
         const min = Math.min(...selectedElements.map(el => el.y));
         const max = Math.max(...selectedElements.map(el => el.y));
         targetValue = (min + max) / 2;
      }

      // 2. 应用
      const nextElements = canvas.elements.map(el => {
        if (!selectedState.selectedIds.includes(el.id) || el.type !== 'svg') return el;
        const node = el as SvgNodeElement;
        
        if (type === 'left' || type === 'right' || type === 'center') {
          return { ...node, x: targetValue };
        } else {
          return { ...node, y: targetValue };
        }
      });

      set({
        canvas: { ...canvas, elements: nextElements },
        undoStack: [...undoStack, previous],
        redoStack: [],
      });
    },

    undo: () => {
      const { undoStack, redoStack, canvas } = get();
      if (!canvas || undoStack.length === 0) {
        return;
      }
      const previous = undoStack[undoStack.length - 1];
      const newUndoStack = undoStack.slice(0, undoStack.length - 1);
      set({
        canvas: previous,
        undoStack: newUndoStack,
        redoStack: [...redoStack, cloneCanvas(canvas)],
      });
    },

    redo: () => {
      const { undoStack, redoStack, canvas } = get();
      if (!canvas || redoStack.length === 0) {
        return;
      }
      const next = redoStack[redoStack.length - 1];
      const newRedoStack = redoStack.slice(0, redoStack.length - 1);
      set({
        canvas: next,
        undoStack: [...undoStack, cloneCanvas(canvas)],
        redoStack: newRedoStack,
      });
    },

    loadAssets: async () => {
      try {
        const assets = await svgAPIService.getAssets();
        set({ assets });
        // 同步一份到本地缓存，方便离线/后端未完成时使用
        saveLocalAssetsSafely(assets);
      } catch (error: any) {
        console.error('加载 SVG 素材库失败:', error);
        set({ error: error.message ?? '加载 SVG 素材库失败' });
        // 回退到本地缓存
        const localAssets = loadLocalAssetsSafely();
        if (localAssets.length > 0) {
          set({ assets: localAssets });
        }
      }
    },

    addAsset: async (asset: Partial<SvgAsset>) => {
      try {
        const created = await svgAPIService.createAsset(asset);
        // ★ API 成功后，重新拉取列表以确保数据同步
        await get().loadAssets();
        return created;
      } catch (error: any) {
        console.error('创建 SVG 素材失败:', error);
        // 后端未实现时，退回到本地临时存储
        const fallback: SvgAsset = {
          // 简单生成一个本地 id，避免与后端冲突
          id: `local_${Date.now()}`,
          name: asset.name ?? `SVG_${Date.now()}`,
          svg_code: asset.svg_code ?? '',
          is_public: asset.is_public ?? false,
          from_ai_prompt: asset.from_ai_prompt,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        } as SvgAsset;

        set((state) => {
          const nextAssets = [...state.assets, fallback];
          saveLocalAssetsSafely(nextAssets);
          return {
            assets: nextAssets,
          };
        });

        // 本地模式认为是“成功”的，返回 fallback，避免前端误认为失败
        return fallback;
      }
    },

    deleteAsset: async (id: string) => {
      try {
        await svgAPIService.deleteAsset(id);
        set((state) => {
          const nextAssets = state.assets.filter((asset) => asset.id !== id);
          saveLocalAssetsSafely(nextAssets);
          return {
            assets: nextAssets,
          };
        });
      } catch (error: any) {
        console.error('删除 SVG 素材失败:', error);
        // 后端未实现时，至少保证本地缓存被删掉
        set((state) => {
          const nextAssets = state.assets.filter((asset) => asset.id !== id);
          saveLocalAssetsSafely(nextAssets);
          return {
            assets: nextAssets,
          };
        });
        // 不再抛出，避免前端认为整个操作失败
      }
    },

    addAiHistory: async (history: Partial<SvgAiHistory>) => {
      try {
        const created = await svgAPIService.createAiHistory(history);
        set((state) => ({
          aiHistories: [...state.aiHistories, created],
        }));
      } catch (error: any) {
        console.error('保存 AI 历史失败:', error);
        set({ error: error.message ?? '保存 AI 历史失败' });
      }
    },

    loadAiHistories: async () => {
      try {
        const list = await svgAPIService.getAiHistoryList();
        set({ aiHistories: list });
      } catch (error: any) {
        console.error('获取 AI 历史失败:', error);
        set({ error: error.message ?? '获取 AI 历史失败' });
      }
    },

    // ★ 修复 addAiSvgToCanvas：放到当前可视区域中心
    // ★ 修复：AI 返回的 SVG 是完整 <svg>...</svg> 标签，需提取 innerContent
    //   保持与手动导入（extractSvgContent → innerContent）一致
    addAiSvgToCanvas: (svgContent: string, name?: string, options?: { x?: number; y?: number }) => {
      const { canvas, viewport, addElement } = get();
      if (!canvas) {
        return;
      }

      // ★ 去除外层 <svg> 标签，只保留内部内容
      // 使用正则而非 DOMParser，因为 AI 生成的 SVG 常有畸形属性
      // （如 <pathd="..."、属性间缺少空格），严格 XML 解析会失败
      let innerContent = svgContent;
      const trimmed = svgContent.trim();
      if (trimmed.startsWith('<svg')) {
        const outerMatch = trimmed.match(/^<svg[^>]*>([\s\S]*)<\/svg>\s*$/i);
        if (outerMatch) {
          innerContent = outerMatch[1];
        }
      }

      // ★ 计算可视区域中心
      const visibleCenterX = viewport.panX + (canvas.width / viewport.zoom) / 2;
      const visibleCenterY = viewport.panY + (canvas.height / viewport.zoom) / 2;

      const element: SvgNodeElement = {
        id: `node_${Date.now()}`,
        type: 'svg',
        sourceType: 'ai',
        svgContent: innerContent,
        originalSvgContent: innerContent,
        // x: options?.x ?? Math.round(visibleCenterX - 100),
        // y: options?.y ?? Math.round(visibleCenterY - 100),
        x: Math.round(viewport.panX),
        y: Math.round(viewport.panY),
        scale: 1,
        rotation: 0,
        opacity: 1,
        name: name ?? 'AI SVG',
      };
      addElement(element);
    },

    // ========== 新增：子元素编辑模式相关 Actions ==========

    /**
     * 进入子元素编辑模式
     */
    enterSubElementEditMode: (nodeId: string) => {
      const { canvas } = get();
      if (!canvas) return;

      const node = canvas.elements.find(el => el.id === nodeId);
      if (!node || node.type !== 'svg') return;

      set({
        isSubElementEditMode: true,
        editingNodeId: nodeId,
        selectedSubElementId: null,
      });
    },

    /**
     * 退出子元素编辑模式
     */
    exitSubElementEditMode: () => {
      set({
        isSubElementEditMode: false,
        editingNodeId: null,
        selectedSubElementId: null,
      });
    },

    /**
     * 选中子元素
     */
    selectSubElement: (subElementId: string | null) => {
      set({ selectedSubElementId: subElementId });
    },


    /**
     * 解析节点的子元素结构
     * 同时提取 SVG 中已有的动画属性
     * 并保存原始 SVG 内容用于恢复
     */
    parseNodeSubElements: (nodeId: string) => {
      const { canvas } = get();
      if (!canvas) return;

      const nodeIndex = canvas.elements.findIndex(el => el.id === nodeId);
      if (nodeIndex === -1) return;

      const node = canvas.elements[nodeIndex];
      if (node.type !== 'svg') return;

      // 如果已经解析过，就不再重复解析
      if (node.subElements && node.subElements.length > 0) return;

      // 1. 解析子元素结构
      const subElements = parseSvgContent(node.svgContent);

      // 2. 提取已有的动画属性（如果 node.animation 未设置或为 none）
      let animation = node.animation;
      if (!animation || !animation.animationType || animation.animationType === 'none') {
        const extractedAnimation = extractAnimationFromSvg(node.svgContent);
        if (extractedAnimation) {
          animation = extractedAnimation;
        }
      }

      // ★ 性能修复：解析子元素是缓存操作，不推 undoStack
      // 避免对大型 canvas 执行昂贵的 cloneCanvas 深拷贝
      const nextElements = [...canvas.elements];
      nextElements[nodeIndex] = {
        ...node,
        subElements,
        animation: animation || node.animation,
        // 关键：首次解析时保存原始 SVG 内容（如果还没有保存过）
        originalSvgContent: node.originalSvgContent || node.svgContent,
      };

      set({
        canvas: { ...canvas, elements: nextElements },
      });
    },

    /**
     * 更新子元素属性
     */
    updateSubElement: (nodeId: string, subElementId: string, attrs: Partial<SvgSubElement['customAttributes']>) => {
      const { canvas, undoStack } = get();
      if (!canvas) return;

      const nodeIndex = canvas.elements.findIndex(el => el.id === nodeId);
      if (nodeIndex === -1) return;

      const node = canvas.elements[nodeIndex];
      if (node.type !== 'svg' || !node.subElements) return;

      const previous = cloneCanvas(canvas);

      // 递归更新子元素
      const updateSubElementInTree = (elements: SvgSubElement[]): SvgSubElement[] => {
        return elements.map(el => {
          if (el.id === subElementId) {
            return {
              ...el,
              customAttributes: { ...el.customAttributes, ...attrs },
            };
          }
          if (el.children) {
            return { ...el, children: updateSubElementInTree(el.children) };
          }
          return el;
        });
      };

      const updatedSubElements = updateSubElementInTree(node.subElements);
      
      // 关键修改：基于原始内容重建 SVG
      const baseContent = node.originalSvgContent || node.svgContent;
      const newSvgContent = rebuildSvgContent(baseContent, updatedSubElements);

      const nextElements = [...canvas.elements];
      nextElements[nodeIndex] = {
        ...node,
        subElements: updatedSubElements,
        svgContent: newSvgContent,
        // 确保 originalSvgContent 始终存在
        originalSvgContent: node.originalSvgContent || node.svgContent,
      };

      set({
        canvas: { ...canvas, elements: nextElements },
        undoStack: [...undoStack, previous],
        redoStack: [],
      });
    },

    /**
     * 恢复子元素的原始属性
     * @param nodeId - 节点 ID
     * @param subElementId - 子元素 ID
     * @param attribute - 要恢复的属性，'all' 表示恢复所有属性
     */
    resetSubElementAttribute: (
      nodeId: string, 
      subElementId: string, 
      attribute: 'fill' | 'stroke' | 'strokeWidth' | 'opacity' | 'textContent' | 'all'
    ) => {
      const { canvas, undoStack } = get();
      if (!canvas) return;

      const nodeIndex = canvas.elements.findIndex(el => el.id === nodeId);
      if (nodeIndex === -1) return;

      const node = canvas.elements[nodeIndex];
      if (node.type !== 'svg' || !node.subElements) return;

      const previous = cloneCanvas(canvas);

      // 递归查找并更新子元素
      const updateSubElements = (elements: SvgSubElement[]): SvgSubElement[] => {
        return elements.map(el => {
          if (el.id === subElementId) {
            // 创建新的 customAttributes 对象
            const newCustomAttributes = { ...el.customAttributes };
            
            if (attribute === 'all') {
              // 恢复所有属性 - 返回空的 customAttributes
              return {
                ...el,
                customAttributes: {},
              };
            }
            
            // 根据属性类型删除对应的自定义值
            switch (attribute) {
              case 'fill':
                delete newCustomAttributes.fill;
                break;
              case 'stroke':
                delete newCustomAttributes.stroke;
                break;
              case 'strokeWidth':
                delete newCustomAttributes.strokeWidth;
                break;
              case 'opacity':
                delete newCustomAttributes.opacity;
                break;
              case 'textContent':
                delete newCustomAttributes.textContent;
                break;
            }
            
            return {
              ...el,
              customAttributes: newCustomAttributes,
            };
          }
          
          // 递归处理子元素
          if (el.children && el.children.length > 0) {
            return {
              ...el,
              children: updateSubElements(el.children),
            };
          }
          
          return el;
        });
      };

      // 更新节点的 subElements
      const updatedSubElements = updateSubElements(node.subElements);
      
      // 关键修复：基于原始内容重建 SVG
      const baseContent = node.originalSvgContent || node.svgContent;
      console.log('resetSubElementAttribute - 基于原始内容重建:', {
        hasOriginal: !!node.originalSvgContent,
        originalPreview: baseContent.substring(0, 100),
        currentPreview: node.svgContent.substring(0, 100),
      });
      
      const newSvgContent = rebuildSvgContent(baseContent, updatedSubElements);
      console.log('resetSubElementAttribute - 重建后内容:', newSvgContent.substring(0, 100));

      const nextElements = [...canvas.elements];
      nextElements[nodeIndex] = {
        ...node,
        subElements: updatedSubElements,
        svgContent: newSvgContent,
      };

      set({
        canvas: { ...canvas, elements: nextElements },
        undoStack: [...undoStack, previous],
        redoStack: [],
      });
    },
    /**
     * 更新节点动画属性
     * 当用户手动修改动画属性时，清除 isOriginalAnimation 标记
     * 但保留 transformOrigin 等已提取的值
     */
    updateNodeAnimation: (nodeId: string, animation: Partial<SvgAnimationProps>) => {
      const { canvas, undoStack } = get();
      if (!canvas) return;

      const nodeIndex = canvas.elements.findIndex(el => el.id === nodeId);
      if (nodeIndex === -1) return;

      const node = canvas.elements[nodeIndex];
      if (node.type !== 'svg') return;

      const previous = cloneCanvas(canvas);
      const nextElements = [...canvas.elements];
      
      // 合并动画属性
      // 保留已有的 transformOrigin（除非用户明确修改）
      // 清除 isOriginalAnimation 标记（因为需要重新生成动画）
      const newAnimation: SvgAnimationProps = {
        ...node.animation,
        ...animation,
        // 只有当用户没有明确设置 transformOrigin 时，才保留原有值
        transformOrigin: animation.transformOrigin !== undefined 
          ? animation.transformOrigin 
          : node.animation?.transformOrigin,
        // 同样保留其他 values
        translateValues: animation.translateValues !== undefined
          ? animation.translateValues
          : node.animation?.translateValues,
        scaleValues: animation.scaleValues !== undefined
          ? animation.scaleValues
          : node.animation?.scaleValues,
        opacityValues: animation.opacityValues !== undefined
          ? animation.opacityValues
          : node.animation?.opacityValues,
        // 用户修改后，不再是原始动画
        isOriginalAnimation: false,
      };

      nextElements[nodeIndex] = {
        ...node,
        animation: newAnimation,
      };

      set({
        canvas: { ...canvas, elements: nextElements },
        undoStack: [...undoStack, previous],
        redoStack: [],
      });
    },

    // ========== 新增：视口操作 ==========

    setZoom: (zoom: number) => {
      const clamped = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, zoom));
      set((state) => ({
        viewport: { ...state.viewport, zoom: clamped },
      }));
    },

    setPan: (panX: number, panY: number) => {
      set((state) => ({
        viewport: { ...state.viewport, panX, panY },
      }));
    },

    /**
     * 以某个 SVG 坐标系中的点为中心进行缩放
     * 确保缩放时鼠标指向的位置不变
     */
    zoomAtPoint: (delta: number, pointX: number, pointY: number) => {
      const { viewport } = get();
      const oldZoom = viewport.zoom;
      const newZoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, oldZoom + delta));
      if (newZoom === oldZoom) return;

      // 缩放前鼠标在 canvas 坐标系的位置: canvasX = panX + pointX / oldZoom
      // 缩放后要保持该位置不变: panX' + pointX / newZoom = panX + pointX / oldZoom
      // => panX' = panX + pointX * (1/oldZoom - 1/newZoom)
      const newPanX = viewport.panX + pointX * (1 / oldZoom - 1 / newZoom);
      const newPanY = viewport.panY + pointY * (1 / oldZoom - 1 / newZoom);

      set({
        viewport: { zoom: newZoom, panX: newPanX, panY: newPanY },
      });
    },

    resetViewport: (vp?) => {
      set({ viewport: vp ?? { zoom: 1, panX: 0, panY: 0 } });
    },

    /**
     * 缩放选中元素的 scale 属性
     * @param delta 正数放大，负数缩小
     */
    scaleSelectedElements: (delta: number) => {
      const { canvas, selectedState, undoStack } = get();
      if (!canvas || selectedState.selectedIds.length === 0) return;

      const previous = cloneCanvas(canvas);
      let changed = false;

      const nextElements = canvas.elements.map((el) => {
        if (!selectedState.selectedIds.includes(el.id)) return el;

        if (el.type === 'svg') {
          const node = el as SvgNodeElement;
          const newScale = Math.max(
            MIN_ELEMENT_SCALE,
            Math.min(MAX_ELEMENT_SCALE, (node.scale ?? 1) + delta)
          );
          if (newScale !== node.scale) changed = true;
          return { ...node, scale: Math.round(newScale * 100) / 100 };
        }
        if (el.type === 'group') {
          const group = el as SvgGroupElement;
          const newScale = Math.max(
            MIN_ELEMENT_SCALE,
            Math.min(MAX_ELEMENT_SCALE, (group.scale ?? 1) + delta)
          );
          if (newScale !== group.scale) changed = true;
          return { ...group, scale: Math.round(newScale * 100) / 100 };
        }
        return el;
      });

      if (!changed) return;

      set({
        canvas: { ...canvas, elements: nextElements },
        undoStack: [...undoStack, previous],
        redoStack: [],
      });
    },

    // ========== 新增：组合/解组 ==========

    /**
     * 将多个元素组合为一个 Group
     * 
     * 原理：
     * 1. 找出所有选中元素
     * 2. 计算包围盒（最小 x/y）作为 group 的位置
     * 3. 将子元素的坐标转换为相对于 group 的坐标
     * 4. 创建 SvgGroupElement，替换掉原来的独立元素
     */
    groupElements: (ids: string[]) => {
      const { canvas, undoStack } = get();
      if (!canvas || ids.length < 2) return;

      const previous = cloneCanvas(canvas);

      // 找出所有要组合的元素（保持原始顺序）
      const toGroup = canvas.elements.filter(el => ids.includes(el.id));
      if (toGroup.length < 2) return;

      // 计算组的位置（取所有元素的最小 x/y）
      let minX = Infinity;
      let minY = Infinity;
      for (const el of toGroup) {
        if (el.type === 'svg') {
          const node = el as SvgNodeElement;
          minX = Math.min(minX, node.x);
          minY = Math.min(minY, node.y);
        } else if (el.type === 'group') {
          const group = el as SvgGroupElement;
          minX = Math.min(minX, group.x);
          minY = Math.min(minY, group.y);
        }
      }

      // 将子元素坐标转换为相对于 group 的坐标
      const updatedChildren = toGroup.map(el => {
        if (el.type === 'svg') {
          const node = el as SvgNodeElement;
          return { ...node, x: node.x - minX, y: node.y - minY };
        }
        if (el.type === 'group') {
          const group = el as SvgGroupElement;
          return { ...group, x: group.x - minX, y: group.y - minY };
        }
        return el;
      });

      const groupId = `group_${Date.now()}`;
      const groupElement: SvgGroupElement = {
        id: groupId,
        type: 'group',
        name: `组合_${toGroup.length}个元素`,
        children: ids,
        x: minX,
        y: minY,
        scale: 1,
        rotation: 0,
        opacity: 1,
      };

      // 构建新的 elements 数组:
      // 1. 保留不在 group 中的元素
      // 2. 在第一个被 group 的元素位置插入 group
      // 3. 被 group 的子元素也保留（坐标已转为相对坐标），但标记为 group 的成员
      const idsSet = new Set(ids);
      const newElements: SvgCanvasElement[] = [];
      let groupInserted = false;

      for (const el of canvas.elements) {
        if (idsSet.has(el.id)) {
          if (!groupInserted) {
            // 在第一个被组合元素的位置插入 group
            newElements.push(groupElement);
            groupInserted = true;
          }
          // 插入坐标已转换的子元素
          const updated = updatedChildren.find(c => c.id === el.id);
          if (updated) newElements.push(updated);
        } else {
          newElements.push(el);
        }
      }

      set({
        canvas: { ...canvas, elements: newElements },
        undoStack: [...undoStack, previous],
        redoStack: [],
        selectedState: {
          selectedIds: [groupId],
          activeElementId: groupId,
        },
      });
    },

    /**
     * 解组 - 将 Group 还原为独立元素
     * 
     * 原理：
     * 1. 找到 group 元素
     * 2. 将子元素的相对坐标转回绝对坐标
     * 3. 移除 group，保留子元素
     */
    ungroupElements: (groupId: string) => {
      const { canvas, undoStack } = get();
      if (!canvas) return;

      const groupIndex = canvas.elements.findIndex(el => el.id === groupId);
      if (groupIndex === -1) return;

      const group = canvas.elements[groupIndex];
      if (group.type !== 'group') return;

      const previous = cloneCanvas(canvas);
      const groupEl = group as SvgGroupElement;

      // 将子元素坐标转回绝对坐标
      const childIds = new Set(groupEl.children);
      const newElements: SvgCanvasElement[] = [];
      const restoredChildIds: string[] = [];

      for (const el of canvas.elements) {
        if (el.id === groupId) {
          // 跳过 group 自身 — 不加入新数组
          continue;
        }
        if (childIds.has(el.id)) {
          // 恢复绝对坐标
          if (el.type === 'svg') {
            const node = el as SvgNodeElement;
            newElements.push({
              ...node,
              x: node.x + groupEl.x,
              y: node.y + groupEl.y,
            });
          } else if (el.type === 'group') {
            const subGroup = el as SvgGroupElement;
            newElements.push({
              ...subGroup,
              x: subGroup.x + groupEl.x,
              y: subGroup.y + groupEl.y,
            });
          } else {
            newElements.push(el);
          }
          restoredChildIds.push(el.id);
        } else {
          newElements.push(el);
        }
      }

      set({
        canvas: { ...canvas, elements: newElements },
        undoStack: [...undoStack, previous],
        redoStack: [],
        selectedState: {
          selectedIds: restoredChildIds,
          activeElementId: restoredChildIds.length === 1 ? restoredChildIds[0] : undefined,
        },
      });
    },

    /**
     * ★ 新增：拆分 SVG 节点为多个独立子元素
     * 
     * 原理：
     * 1. 解析 SVG 内容，提取每个顶层可视图形元素（path、circle、rect、g 等）
     * 2. 为每个子元素创建独立的 SvgNodeElement，继承父元素的位置和缩放
     * 3. 移除原始节点，插入新的子节点
     * 4. 如果 SVG 有 <defs>（渐变、滤镜等），每个子元素都会携带一份
     * 
     * 限制：
     * - 只拆分顶层子元素（不递归拆分嵌套的 <g>）
     * - 拆分后子元素共享同一个位置（因为无法精确获取每个子元素的绝对位置）
     * - 如果只有1个子元素或没有子元素，不执行拆分
     */
    splitElement: (nodeId: string) => {
      const { canvas, undoStack } = get();
      if (!canvas) return;

      const nodeIndex = canvas.elements.findIndex(el => el.id === nodeId);
      if (nodeIndex === -1) return;

      const node = canvas.elements[nodeIndex];
      if (node.type !== 'svg') return;

      const svgNode = node as SvgNodeElement;
      const children = extractSplitChildren(svgNode.svgContent);

      // 如果只有 0 或 1 个子元素，不需要拆分
      if (children.length <= 1) {
        console.warn('splitElement: SVG 只有', children.length, '个可视子元素，无法拆分');
        return;
      }

      const previous = cloneCanvas(canvas);
      const timestamp = Date.now();

      // 为每个子元素创建独立的 SvgNodeElement
      const newNodes: SvgNodeElement[] = children.map((child, index) => {
        // 如果有 defs，把 defs 拼到子元素内容前面
        const content = child.defsContent
          ? `${child.defsContent}${child.svgContent}`
          : child.svgContent;

        return {
          id: `node_${timestamp}_${index}`,
          type: 'svg' as const,
          sourceType: svgNode.sourceType,
          svgContent: content,
          originalSvgContent: content,
          x: svgNode.x,
          y: svgNode.y,
          scale: svgNode.scale,
          rotation: svgNode.rotation,
          opacity: svgNode.opacity,
          name: `${svgNode.name || 'SVG'}_${child.name}`,
        };
      });

      // 构建新的 elements 数组：在原位置替换
      const newElements: SvgCanvasElement[] = [];
      for (let i = 0; i < canvas.elements.length; i++) {
        if (i === nodeIndex) {
          // 在原始节点位置插入所有拆分后的子节点
          newElements.push(...newNodes);
        } else {
          newElements.push(canvas.elements[i]);
        }
      }

      const newIds = newNodes.map(n => n.id);

      set({
        canvas: { ...canvas, elements: newElements },
        undoStack: [...undoStack, previous],
        redoStack: [],
        selectedState: {
          selectedIds: newIds,
          activeElementId: newIds.length === 1 ? newIds[0] : undefined,
        },
        // 退出子元素编辑模式（如果在的话）
        isSubElementEditMode: false,
        editingNodeId: null,
        selectedSubElementId: null,
      });
    },
  }))
);